import React, { useEffect, useState } from 'react'
import Header from './layout/Header'
import Footer from './layout/Footer'
import aboutUsImage from './aboutus.jpg'
import our_mission_img from './our_mission_img.svg'
import about_image_2 from './about_image_2.jpg'
import * as websiteService from './services/website.services'
import { sanitizeHtml } from '../../utils/commonfunction'

function AboutUs() {
    const [pageData, setPageData] = useState(null);

    useEffect(() => {
        const fetchCms = async () => {
            try {
                // Try fetching 'about' slug first, fallback to 'about-us' if not found
                let response = await websiteService.CMSPage('about');
                if (response.success && response.data && response.data.sections) {
                    setPageData(response.data);
                } else {
                    response = await websiteService.CMSPage('about-us');
                    if (response.success && response.data && response.data.sections) {
                        setPageData(response.data);
                    }
                }
            } catch (error) {
                console.error("Failed to fetch About Us content", error);
            }
        };
        fetchCms();
    }, []);

    const sections = pageData?.sections || [];
    const sec1 = sections[0];
    const sec2 = sections[1];
    const sec3 = sections[2];

    const renderDynamicDetailContent = (htmlString) => {
        if (!htmlString) return null;
        
        const cleanHtml = sanitizeHtml(htmlString);
        let paragraphs = [];
        if (cleanHtml.includes('<p>')) {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = cleanHtml;
            const pTags = Array.from(tempDiv.querySelectorAll('p'));
            if (pTags.length > 0) {
                paragraphs = pTags.map(p => p.innerHTML.trim()).filter(Boolean);
            } else {
                paragraphs = [cleanHtml];
            }
        } else {
            paragraphs = cleanHtml.split(/\n\s*\n/).map(p => p.trim()).filter(Boolean);
        }
        
        if (paragraphs.length === 0) {
            return null;
        }
        
        return (
            <div className='about-detail-content d-flex flex-column justify-content-center gap-4 ps-lg-4'>
                {paragraphs.map((para, index) => {
                    if (index === 1) {
                        return (
                            <div className='about-detail-highlight' key={index}>
                                <p 
                                    className='about-detail-highlight-text mb-0' 
                                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(para) }} 
                                />
                            </div>
                        );
                    } else {
                        return (
                            <p 
                                className={`about-detail-text ${index === paragraphs.length - 1 ? 'mb-0' : ''}`} 
                                key={index}
                                dangerouslySetInnerHTML={{ __html: sanitizeHtml(para) }} 
                            />
                        );
                    }
                })}
            </div>
        );
    };

    const renderSubsections = (subsections) => {
        if (!subsections || subsections.length === 0) return null;
        return (
            <div className="subsections-container margin-top-50 w-100">
                {subsections.map((sub, index) => {
                    const isEven = index % 2 === 0;
                    return (
                        <div className={`row align-items-center mb-5 ${isEven ? 'flex-row' : 'flex-row-reverse'}`} key={index}>
                            {sub.image && (
                                <div className="col-12 col-lg-5 text-center mb-3 mb-lg-0">
                                    <img 
                                        src={sub.image} 
                                        alt={`Subsection ${index + 1}`} 
                                        className="img-fluid rounded shadow-sm"
                                        style={{ maxHeight: "250px", objectFit: "cover", width: "100%", borderRadius: "12px" }}
                                    />
                                </div>
                            )}
                            <div className={`col-12 ${sub.image ? 'col-lg-7' : 'col-lg-12'}`}>
                                <div className="subsection-content p-3 text-left">
                                    {sub.shortDescription && (
                                        <h4 className="subsection-short-desc mb-3" style={{ color: "var(--main-primary)", fontWeight: "600" }}>
                                            {sub.shortDescription}
                                        </h4>
                                    )}
                                    {sub.longDescription && (
                                        <p className="subsection-long-desc text-muted mb-0" style={{ lineHeight: "1.7" }}>
                                            {sub.longDescription}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <React.Fragment>
            <Header />
            <main>

                <section className='about_us_banner'>
                    <div className='container'>
                        <div className='about-hero-content d-flex flex-column justify-content-center'>
                            <div className='about-hero-copy d-flex flex-column align-items-center text-center gap-3'>
                                <h1 className='about-hero-title mb-0'>{sec1?.title || 'About mNetrics'}</h1>
                                <div className='about-hero-text mb-0' dangerouslySetInnerHTML={{ __html: sanitizeHtml(sec1?.longDescription || `
                                    mNetrics (pronounced "Netrics", with a silent "m") is a modern netball performance app designed
                                    to bring clarity, insight, and structure to the game at every level. Built by netball parents
                                    and players and shaped by active involvement in the UK netball community, mNetrics captures live
                                    match data and transforms it into meaningful performance insights.
                                `) }} />
                            </div>

                            <img src={sec1?.image || aboutUsImage} alt='About mNetrics' className='about-hero-image' />

                            {sec1?.subsections && renderSubsections(sec1.subsections)}
                        </div>
                    </div>
                </section>

                <section className='our_mission position-relative margin-top-100'>
                    <div className='container'>
                        <div className='row justify-content-center align-items-center'>
                            <div className='col-12 col-lg-7'>
                                <div className='our-mission-content pe-xl-5'>
                                    <h1 className='about-hero-title mb-2'>{sec2?.title || 'Our mission'}</h1>
                                    {sec2?.longDescription ? (
                                        <div className='our-mission-text' dangerouslySetInnerHTML={{ __html: sanitizeHtml(sec2.longDescription) }} />
                                    ) : (
                                        <>
                                            <p className='our-mission-text'>
                                                Our mission is simple: to make performance data accessible, understandable, and impactful across grassroots and competitive netball.
                                            </p>
                                            <p className='our-mission-text mb-0'>
                                                Whether it&apos;s tracking live scores during a match, analysing team performance over time, or reviewing individual player contributions, mNetrics helps users see the game in a way that goes beyond memory and intuition.
                                            </p>
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className='col-12 col-lg-5'>
                                <img src={sec2?.image || our_mission_img} alt='Our Mission' className='about-hero-image' />
                            </div>
                        </div>

                        {sec2?.subsections && renderSubsections(sec2.subsections)}
                    </div>
                </section>

                <section className='about-detail margin-top-100'>
                    <div className='container'>
                        <div className="row justify-content-center align-items-center">
                            <div className="row flex-column-reverse flex-lg-row justify-content-center align-items-center row-gap-4 w-100">
                                <div className='col-12 col-lg-6'>
                                    <img src={sec3?.image || about_image_2} alt='About mNetrics' className='about-detail-image' />
                                </div>
                                <div className='col-12 col-lg-6'>
                                    {sec3?.longDescription ? (
                                        renderDynamicDetailContent(sec3.longDescription)
                                    ) : (
                                        <div className='about-detail-content d-flex flex-column justify-content-center gap-4 ps-lg-4'>
                                            <p className='about-detail-text'>
                                                We believe that data should not be reserved for elite sport alone. Every team, every player, and every supporter should have access to tools that support development, engagement, and enjoyment of the game. mNetrics bridges that gap by providing a simple yet powerful platform that fits naturally into match day and season-long tracking.
                                            </p>
                                            <div className='about-detail-highlight'>
                                                <p className='about-detail-highlight-text mb-0'>
                                                    At its core, mNetrics is about empowering better decisions and deeper understanding. Coaches can make informed tactical choices, players can track their growth, parents can stay connected to performance, and clubs/schools can build a clearer picture of progress across teams.
                                                </p>
                                            </div>
                                            <p className='about-detail-text mb-0'>
                                                As we continue to grow, our focus remains on building a platform that evolves with the needs of the netball community, combining intuitive design, powerful analytics, and a commitment to making the game more transparent, engaging, and data-driven.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {sec3?.subsections && renderSubsections(sec3.subsections)}
                    </div>
                </section>

            </main>
            <Footer />
        </React.Fragment>
    )
}

export default AboutUs